import { getPromptFromViewIdPromptId } from './CurrentTemplateSlice';
import Paths from '../../../../Paths';
import Constants from '../../../../common/Constants';

/**
 * Formats a URL that will direct a user to editing a specific prompt
 * @param templateId the template being edited
 * @param locale the locale in which the template should be loaded
 * @param viewId the view being edited
 * @param promptId the prompt to navigate to
 * @returns the URL that will load the corresponding template
 */

export const generateTemplatePromptUrl = (templateId: string, locale = Constants.DEFAULT_LOCALE, viewId = '', promptId = ''): string => {
    if (!templateId) {
        // If there's no template ID, just link back to the list page
        return Paths.MANAGE_TEMPLATES_PATH;
    }

    const params = new URLSearchParams();
    params.append('locale', locale);
    if (viewId) {
        params.append('viewId', viewId);
    }
    if (promptId) {
        params.append('promptId', promptId);
    }
    const newUrl = `${Paths.EDIT_TEMPLATE_PATH}/${templateId}?${params.toString()}`;

    return newUrl;
};
/**
 * Formats a URL that will direct a user to editing a specific module
 * @param templateId the template being edited
 * @param locale the locale in which the template should be loaded
 * @param viewId the view being edited
 * @param moduleId the module to navigate to
 * @returns the URL that will load the corresponding template
 */

export const generateTemplateModuleUrl = (templateId: string, locale = Constants.DEFAULT_LOCALE, viewId = '', moduleId = ''): string => {
    if (!templateId) {
        // If there's no template ID, just link back to the list page
        return Paths.MANAGE_TEMPLATES_PATH;
    }

    const params = new URLSearchParams();
    params.append('locale', locale);
    if (viewId) {
        params.append('viewId', viewId);
    }
    if (moduleId) {
        params.append('moduleId', moduleId);
    }
    const newUrl = `${Paths.EDIT_TEMPLATE_PATH}/${templateId}?${params.toString()}`;

    return newUrl;
};

/**
 * Generate a template-specific URL with a suffix (for sharing, editing assessment type metadata, etc.)
 * @param templateId the template being edited
 * @param suffix the suffix that identifies the component to route to
 * @param locale if this URL is the first one that the user loads, the template will be loaded in this locale
 * @returns a URL for the template, pointing to the component denoted by the suffix
 */
export const generateTemplateUrlWithSuffix = (templateId: string, suffix: string, locale = Constants.DEFAULT_LOCALE): string => {
    if (!templateId) {
        // If there's no template ID, just link back to the list page
        return Paths.MANAGE_TEMPLATES_PATH;
    }

    const params = new URLSearchParams();
    params.append('locale', locale);

    const newUrl = `${Paths.EDIT_TEMPLATE_PATH}/${templateId}${suffix}?${params.toString()}`;

    return newUrl;
};

/**
 * Converts promptId search param to module search param, for use in side nav
 * @param searchParam the current search params
 * @returns the same set of search params, but with the moduleId containing the prompt
 */
export const convertSearchParamTemplatePromptIdToModule = (allSearchParams: string): string => {
    const parsedSearchParams = new URLSearchParams(allSearchParams);

    const viewId = parsedSearchParams.get('viewId');
    const promptId = parsedSearchParams.get('promptId');

    // No prompt ID --> nothing to transform in the URL
    if (!promptId) {
        return `?${parsedSearchParams.toString()}`;
    }

    const moduleId = getPromptFromViewIdPromptId(viewId, promptId)?.containingModule?.moduleId;

    parsedSearchParams.delete('promptId');
    if (moduleId) {
        parsedSearchParams.append('moduleId', moduleId);
    }

    return `?${parsedSearchParams.toString()}`;
};
